/**
 * crossrefService.ts - Integração com API CrossRef
 * Bigwa v1.0 - Janeiro 2026
 * 
 * API Docs: https://api.crossref.org/swagger-ui/index.html
 * Rate limit: 50 req/seg com email (polite pool)
 */

import {
  WorkCandidate,
  Author,
  mapCrossRefType,
  normalizeWorkKey
} from '../../types/capturaInteligente';
import { logger } from '../../utils/logger';
import { generateNormalizedKey } from '../../utils/normalizedKey';
import { createRateLimiter, type RateLimiter } from '../../core/http/RateLimiter';
import type { Work } from '../../types/work';

const CROSSREF_BASE_URL = 'https://api.crossref.org/works';
const CROSSREF_EMAIL = 'contato@bigwa.app'; // Para polite pool

interface CrossRefAuthor {
  given?: string;
  family?: string;
  name?: string;
}

interface CrossRefItem {
  DOI?: string;
  title?: string[];
  author?: CrossRefAuthor[];
  published?: { 'date-parts'?: number[][] };
  'published-print'?: { 'date-parts'?: number[][] };
  'published-online'?: { 'date-parts'?: number[][] };
  type?: string;
  'container-title'?: string[];
  publisher?: string;
  volume?: string;
  issue?: string;
  page?: string;
  abstract?: string;
  URL?: string;
  'is-referenced-by-count'?: number;
}

function parseAuthors(crossRefAuthors: CrossRefAuthor[] | undefined): Author[] {
  if (!crossRefAuthors || crossRefAuthors.length === 0) {
    return [{ firstName: '', lastName: 'Desconhecido' }];
  }
  
  return crossRefAuthors.map(a => ({
    firstName: a.given || '',
    lastName: a.family || a.name || 'Desconhecido',
  }));
}

function parseYear(item: CrossRefItem): number {
  const dateParts = 
    item.published?.['date-parts']?.[0] ||
    item['published-print']?.['date-parts']?.[0] ||
    item['published-online']?.['date-parts']?.[0];
  
  return dateParts?.[0] || new Date().getFullYear();
}

function crossRefItemToCandidate(item: CrossRefItem, relevanceScore = 50, relevanceReason = ''): WorkCandidate {
  const authors = parseAuthors(item.author);
  const year = parseYear(item);
  const title = item.title?.[0] || 'Sem título';
  
  return {
    id: item.DOI || `crossref-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    title,
    authors,
    year,
    type: mapCrossRefType(item.type || 'article'),
    doi: item.DOI,
    journal: item['container-title']?.[0],
    publisher: item.publisher,
    volume: item.volume,
    issue: item.issue,
    pages: item.page,
    abstract: item.abstract?.replace(/<[^>]*>/g, ''), // Remove HTML tags
    url: item.URL,
    relevanceScore,
    relevanceReason,
    source: 'crossref',
    normalizedKey: normalizeWorkKey(
      authors.map(a => a.lastName),
      year,
      title
    ),
    citationCount: item['is-referenced-by-count'],
  };
}

/**
 * Busca principal por autor, ano e título
 */
export async function searchByAuthorYearTitle(
  author: string,
  year: number | null,
  title: string
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'query.bibliographic': `${author} ${title}`.trim(),
      'rows': '10',
      'sort': 'relevance',
      'mailto': CROSSREF_EMAIL,
    });
    
    if (year) {
      params.append('filter', `from-pub-date:${year - 1},until-pub-date:${year + 1}`);
    }
    
    const response = await fetch(`${CROSSREF_BASE_URL}?${params}`);
    
    if (!response.ok) {
      logger.error('CrossrefService', 'Error:', response.status);
      return [];
    }
    
    const data = await response.json();
    const items: CrossRefItem[] = data.message?.items || [];
    
    return items.map((item, index) => {
      // Calcular relevância baseada na posição e match
      const positionScore = Math.max(0, 100 - (index * 10));
      const yearMatch = year && parseYear(item) === year ? 20 : 0;
      const relevance = Math.min(100, positionScore + yearMatch);
      
      return crossRefItemToCandidate(
        item, 
        relevance,
        index === 0 ? 'Melhor correspondência' : `Resultado #${index + 1}`
      );
    });
  } catch (error) {
    logger.error('CrossrefService', 'Search error:', error);
    return [];
  }
}

/**
 * Busca por DOI específico
 */
export async function searchByDOI(doi: string): Promise<WorkCandidate | null> {
  try {
    const cleanDoi = doi.replace(/^https?:\/\/doi\.org\//, '');
    const response = await fetch(`${CROSSREF_BASE_URL}/${encodeURIComponent(cleanDoi)}?mailto=${CROSSREF_EMAIL}`);
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    const item: CrossRefItem = data.message;
    
    if (!item) return null;
    
    return crossRefItemToCandidate(item, 100, 'DOI exato');
  } catch (error) {
    logger.error('CrossrefService', 'DOI search error:', error);
    return null;
  }
}

/**
 * Busca outras obras do mesmo autor
 */
export async function searchByAuthor(
  authorLastName: string,
  excludeYear?: number,
  limit = 10
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'query.author': authorLastName,
      'rows': limit.toString(),
      'sort': 'published',
      'order': 'desc',
      'mailto': CROSSREF_EMAIL,
    });
    
    const response = await fetch(`${CROSSREF_BASE_URL}?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const items: CrossRefItem[] = data.message?.items || [];
    
    return items
      .filter(item => !excludeYear || parseYear(item) !== excludeYear)
      .map(item => crossRefItemToCandidate(
        item,
        70,
        `Outra obra de ${authorLastName}`
      ));
  } catch (error) {
    logger.error('CrossrefService', 'Author search error:', error);
    return [];
  }
}

/**
 * Busca por termo genérico (título parcial, palavras-chave)
 */
export async function searchGeneric(
  query: string,
  limit = 15
): Promise<WorkCandidate[]> {
  try {
    const params = new URLSearchParams({
      'query': query,
      'rows': limit.toString(),
      'sort': 'relevance',
      'mailto': CROSSREF_EMAIL,
    });
    
    const response = await fetch(`${CROSSREF_BASE_URL}?${params}`);
    
    if (!response.ok) {
      return [];
    }
    
    const data = await response.json();
    const items: CrossRefItem[] = data.message?.items || [];
    
    return items.map((item, index) => crossRefItemToCandidate(
      item,
      Math.max(30, 80 - (index * 5)),
      'Termo relacionado'
    ));
  } catch (error) {
    logger.error('CrossrefService', 'Generic search error:', error);
    return [];
  }
}

// ============================================================================
// Camada 1 — Produtor de citedReferences (Horizonte Onda 2, Etapa A)
// ============================================================================

/**
 * Uma entrada de `citedReferences`, na shape EXATA do slot `Work.citedReferences`
 * (`work.ts:258`) — derivada do tipo para não driftar da fonte da verdade.
 */
export type CitedReference = NonNullable<Work['citedReferences']>[number];

/** Shape parcial e heterogênea de uma entrada de `message.reference[]` do Crossref. */
interface CrossRefReference {
  author?: unknown;
  year?: unknown;
  'article-title'?: unknown;
  'volume-title'?: unknown;
  'journal-title'?: unknown;
  'series-title'?: unknown;
  unstructured?: unknown;
}

/** Dependências injetáveis (testes); produção usa os defaults. */
export interface GetCitedReferencesDeps {
  /** Substitui `fetch` (default: `fetch` global). */
  fetchImpl?: (url: string) => Promise<Response>;
  /** Substitui o rate limiter (default: preset `CROSSREF` lazy). */
  limiter?: { acquire(): Promise<void> };
}

/**
 * Limiter do polite pool Crossref (AC-13). Lazy: só nasce na 1ª chamada real,
 * para que testes que injetam um limiter não criem o timer de refill do preset.
 */
let crossrefLimiter: RateLimiter | null = null;
function getCrossrefLimiter(): RateLimiter {
  return (crossrefLimiter ??= createRateLimiter('CROSSREF'));
}

/** Extrai a primeira string útil de um campo Crossref que pode ser string ou string[]. */
function firstString(value: unknown): string | undefined {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : undefined;
  return typeof value === 'string' ? value : undefined;
}

/** Ano plausível de publicação (1500–2099), para best-effort sobre entradas cruas. */
const YEAR_RE = /\b(1[5-9]\d{2}|20\d{2})\b/;

/**
 * Mapeia uma entrada heterogênea de `reference[]` para a shape de `citedReferences`.
 *
 * Degradação graciosa (AC-08): entrada só-`unstructured` é mapeada best-effort
 * (título = string crua; ano extraído por regex se houver) — **nunca** lança nem
 * é descartada silenciosamente (balde (c) declarado). Defensivo quanto a
 * string × array nos campos, porque a shape do Crossref varia entre entradas.
 *
 * `normalizedKey` vem do gerador canônico do módulo neutro (`normalizedKey.ts`),
 * idêntico ao comparador `:89` da Biblioteca — nunca normalização ad-hoc (AC-09).
 */
function mapReferenceEntry(entry: CrossRefReference): CitedReference {
  const unstructured = firstString(entry.unstructured);

  const authorStr = firstString(entry.author);
  const authors = authorStr ? [authorStr] : [];

  let year: number | null = null;
  const parsed = entry.year != null ? parseInt(String(entry.year), 10) : NaN;
  if (Number.isFinite(parsed)) {
    year = parsed;
  } else if (unstructured) {
    const match = unstructured.match(YEAR_RE);
    if (match) year = parseInt(match[1], 10);
  }

  const title =
    firstString(entry['article-title']) ??
    firstString(entry['volume-title']) ??
    firstString(entry['journal-title']) ??
    firstString(entry['series-title']) ??
    unstructured ??
    '';

  return { authors, year, title, normalizedKey: generateNormalizedKey(authors, year) };
}

/**
 * Busca as referências citadas (`message.reference[]`) de um DOI no Crossref e
 * mapeia cada entrada para a shape de `Work.citedReferences`.
 *
 * - Passa por `RateLimiter(CROSSREF)` antes do fetch (AC-13; polite pool, R$0).
 * - `reference[]` ausente/vazio → `[]` (balde (b), AC-10; **não** é erro).
 * - Rede / HTTP 4xx-5xx → **lança** (AC-15: nunca silencioso), para o orquestrador
 *   da Camada 1 tratar (toast/painel + rollback) — diverge de `searchByDOI`, que
 *   engole o erro.
 *
 * @param doi  DOI da obra (com ou sem prefixo `https://doi.org/`).
 * @param deps Dependências injetáveis (testes); produção usa os defaults.
 */
export async function getCitedReferences(
  doi: string,
  deps: GetCitedReferencesDeps = {},
): Promise<CitedReference[]> {
  const limiter = deps.limiter ?? getCrossrefLimiter();
  const doFetch = deps.fetchImpl ?? ((url: string) => fetch(url));

  const cleanDoi = doi.replace(/^https?:\/\/doi\.org\//, '');
  const url = `${CROSSREF_BASE_URL}/${encodeURIComponent(cleanDoi)}?mailto=${CROSSREF_EMAIL}`;

  await limiter.acquire();
  const response = await doFetch(url);

  if (!response.ok) {
    throw new Error(`Crossref ${response.status} ao buscar referências de ${cleanDoi}`);
  }

  const data = await response.json();
  const references: CrossRefReference[] = Array.isArray(data?.message?.reference)
    ? data.message.reference
    : [];

  return references.map(mapReferenceEntry);
}

export const crossrefService = {
  searchByAuthorYearTitle,
  searchByDOI,
  searchByAuthor,
  searchGeneric,
  getCitedReferences,
};
